// playground — wireframe skin module
// Token disposition: color tokens overridden to wf-* namespace; spacing/sizing pass-through

export const styles = `
  :host {
    display: block;
    background: var(--run-wf-surface);
    color: var(--run-wf-text);
    border: 1px dashed var(--run-wf-border);
    border-radius: var(--run-playground-border-radius, var(--run-radius-semantic-card));
    overflow: hidden;
  }

  [part="container"] {
    display: flex;
    flex-direction: column;
    gap: var(--run-playground-gap, var(--run-space-semantic-xs));
  }

  [part="header"] {
    display: flex;
    align-items: center;
    gap: var(--run-playground-toolbar-gap, var(--run-space-semantic-xs));
    background: var(--run-wf-surface-subtle);
    border-bottom: 1px dashed var(--run-wf-border);
    padding-block: var(--run-playground-toolbar-padding-block, var(--run-space-semantic-xs));
    padding-inline: var(--run-playground-toolbar-padding-inline, var(--run-space-semantic-xs));
  }

  .header-tag {
    flex: 1;
    font-family: var(--run-wf-font-family);
    font-weight: 400;
    font-size: var(--run-playground-tab-font-size, var(--run-font-size-sm));
  }

  .main-grid {
    display: grid;
    grid-template-columns: 1fr min(320px, var(--run-playground-controls-width, 320px));
    gap: var(--run-playground-gap, var(--run-space-semantic-xs));
    padding-inline: var(--run-playground-padding-inline, var(--run-space-semantic-sm));
  }

  :host([mode="full"]) .main-grid {
    grid-template-columns: 1fr min(320px, var(--run-playground-controls-width, 320px));
  }

  @media (max-width: 640px) {
    .main-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    :host([mode="full"]) .main-grid {
      grid-template-columns: 1fr;
    }
  }

  [part="preview"] {
    min-height: var(--run-playground-preview-min-height, 200px);
    background: var(--run-wf-surface);
    border: 1px dashed var(--run-wf-border);
    border-radius: var(--run-playground-preview-border-radius, var(--run-radius-semantic-card));
    padding-block: var(--run-playground-preview-padding-block, var(--run-space-semantic-xs));
    padding-inline: var(--run-playground-preview-padding-inline, var(--run-space-semantic-xs));
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: auto;
  }

  [part="preview"][aria-busy="true"] {
    opacity: 0.6;
  }

  .preview-host {
    display: contents;
  }

  .preview-error {
    color: var(--run-wf-text);
    font-family: var(--run-wf-font-family);
    font-weight: 400;
    font-size: var(--run-playground-tab-font-size, var(--run-font-size-sm));
    text-align: center;
    padding: var(--run-playground-canvas-padding-block, var(--run-space-semantic-sm));
  }

  .preview-loading {
    font-family: var(--run-wf-font-family);
    font-weight: 400;
    font-size: var(--run-playground-tab-font-size, var(--run-font-size-sm));
    opacity: 0.5;
  }

  [part="controls"] {
    display: flex;
    flex-direction: column;
    gap: var(--run-playground-gap, var(--run-space-semantic-xs));
    padding-block: var(--run-playground-canvas-padding-block, var(--run-space-semantic-sm));
  }

  [aria-label="Component controls"] {
    display: flex;
    flex-direction: column;
    gap: var(--run-playground-toolbar-gap, var(--run-space-semantic-xs));
    flex: 1;
  }

  .control-group {
    display: flex;
    flex-direction: column;
    gap: calc(var(--run-playground-toolbar-gap, var(--run-space-semantic-xs)) / 2);
  }

  .control-label {
    font-family: var(--run-wf-font-family);
    font-weight: 400;
    font-size: var(--run-playground-tab-font-size, var(--run-font-size-sm));
  }

  .control-description {
    font-family: var(--run-wf-font-family);
    font-weight: 400;
    font-size: var(--run-playground-tab-font-size, var(--run-font-size-sm));
    opacity: 0.65;
    margin-block-start: 0;
    margin-block-end: calc(var(--run-playground-toolbar-gap, var(--run-space-semantic-xs)) / 2);
  }

  .control-error-indicator {
    font-family: var(--run-wf-font-family);
    font-weight: 400;
    font-size: var(--run-playground-tab-font-size, var(--run-font-size-sm));
    color: var(--run-wf-text);
    opacity: 0.8;
  }

  .controls-empty {
    font-family: var(--run-wf-font-family);
    font-weight: 400;
    font-size: var(--run-playground-tab-font-size, var(--run-font-size-sm));
    opacity: 0.5;
  }

  .reset-row {
    padding-block-start: var(--run-playground-toolbar-padding-block, var(--run-space-semantic-xs));
    border-top: 1px dashed var(--run-wf-border);
  }

  [part="code"] {
    background: var(--run-wf-surface-sunken);
    border: 1px dashed var(--run-wf-border);
    border-radius: var(--run-playground-editor-border-radius, var(--run-radius-semantic-card));
    padding-block: var(--run-playground-editor-padding-block, var(--run-space-semantic-xs));
    padding-inline: var(--run-playground-editor-padding-inline, var(--run-space-semantic-xs));
    margin-inline: var(--run-playground-padding-inline, var(--run-space-semantic-sm));
    font-family: var(--run-wf-font-family);
    font-weight: 400;
    font-size: var(--run-playground-editor-font-size, var(--run-font-size-sm));
    line-height: var(--run-playground-editor-line-height, var(--run-font-semantic-line-height-heading));
  }

  .auxiliary-tabs {
    margin-inline: var(--run-playground-padding-inline, var(--run-space-semantic-sm));
  }

  .tab-list {
    display: flex;
    border-bottom: 1px dashed var(--run-wf-border);
    gap: 0;
  }

  .tab-btn {
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    color: var(--run-wf-text-subtle);
    font-family: var(--run-wf-font-family);
    font-weight: 400;
    font-size: var(--run-playground-tab-font-size, var(--run-font-size-sm));
    padding-block: var(--run-playground-tab-padding-block, var(--run-space-semantic-xs));
    padding-inline: var(--run-playground-tab-padding-inline, var(--run-space-semantic-xs));
    margin-block-end: -1px;
  }

  .tab-btn:hover {
    color: var(--run-wf-text);
  }

  .tab-btn[aria-selected="true"] {
    color: var(--run-wf-text);
    background: var(--run-wf-surface);
    border-bottom-color: var(--run-wf-border);
  }

  .tab-panel {
    padding-block: var(--run-playground-canvas-padding-block, var(--run-space-semantic-sm));
  }

  [part="events"],
  [part="a11y"],
  [part="performance"],
  [part="tokens"] {
    padding-block: var(--run-playground-canvas-padding-block, var(--run-space-semantic-sm));
    font-family: var(--run-wf-font-family);
    font-weight: 400;
    font-size: var(--run-playground-tab-font-size, var(--run-font-size-sm));
  }

  .event-log-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--run-playground-toolbar-gap, var(--run-space-semantic-xs));
    max-height: 200px;
    overflow-y: auto;
  }

  .event-log-item {
    font-family: var(--run-wf-font-family);
    font-weight: 400;
    font-size: var(--run-playground-editor-font-size, var(--run-font-size-sm));
    padding-block: var(--run-playground-toolbar-padding-block, var(--run-space-semantic-xs));
    padding-inline: var(--run-playground-toolbar-padding-inline, var(--run-space-semantic-xs));
    background: var(--run-wf-surface-sunken);
    border-radius: var(--run-playground-editor-border-radius, var(--run-radius-semantic-card));
  }

  .event-log-empty {
    opacity: 0.5;
  }

  .panel-coming-soon {
    opacity: 0.5;
    font-style: italic;
  }

  .css-props-panel,
  .slots-panel {
    padding-block-start: var(--run-playground-toolbar-padding-block, var(--run-space-semantic-xs));
    border-top: 1px dashed var(--run-wf-border);
    margin-block-start: var(--run-playground-toolbar-gap, var(--run-space-semantic-xs));
    font-family: var(--run-wf-font-family);
    font-weight: 400;
    font-size: var(--run-playground-tab-font-size, var(--run-font-size-sm));
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .footer-slot {
    padding-inline: var(--run-playground-padding-inline, var(--run-space-semantic-sm));
    padding-block-end: var(--run-playground-padding-block, var(--run-space-semantic-sm));
  }
`;

export function render(host) {
  const {
    tag,
    mode,
    contractData,
    _compoundState,
    _errorMessage,
    _resolvedControls,
    _generatedCode,
    _eventLog,
    _isDirty,
    showCode,
    showEvents,
    showA11y,
    showPerformance,
    showTokenCompliance,
    showCssProps,
    showSlots,
    codeFrameworks,
  } = host;

  const isLoading = _compoundState === 'loading';
  const isError = _compoundState === 'error';
  const controls = Array.isArray(_resolvedControls) ? _resolvedControls : [];
  const eventLog = Array.isArray(_eventLog) ? _eventLog : [];
  const frameworks = Array.isArray(codeFrameworks) ? codeFrameworks : ['html'];
  const activeFramework = frameworks[0] ?? 'html';
  const codeText = (_generatedCode && _generatedCode[activeFramework]) ?? '';

  // ── Header ────────────────────────────────────────────────────────────────
  const badgeHtml = (contractData && contractData.status)
    ? `<run-badge>${contractData.status}</run-badge>`
    : '';

  const headerHtml = `
    <div part="header">
      <slot name="header">
        <span class="header-tag">${tag ? `&lt;${tag}&gt;` : 'run-playground'}</span>
        ${badgeHtml}
        <run-icon-button data-action="copy" label="Copy shareable link"></run-icon-button>
      </slot>
    </div>
  `;

  // ── Controls panel ────────────────────────────────────────────────────────
  const controlGroupsHtml = controls.length === 0
    ? `<span class="controls-empty">No controls available.</span>`
    : controls.map((ctrl, i) => {
        const groupId = `ctrl-group-${i}`;
        const labelId = `ctrl-label-${i}`;
        const disabled = isLoading ? 'disabled' : '';
        const errorIndicator = ctrl.hasError
          ? `<span class="control-error-indicator" aria-live="polite">Value error</span>`
          : '';
        const descriptionHtml = ctrl.description
          ? `<p class="control-description" id="ctrl-desc-${i}">${ctrl.description}</p>`
          : '';

        let controlHtml = '';
        if (ctrl.controlType === 'checkbox') {
          controlHtml = `<run-checkbox
            id="ctrl-${i}"
            ${ctrl.currentValue ? 'checked' : ''}
            ${disabled}
            aria-labelledby="${labelId}"
            data-prop="${ctrl.name}"
          ></run-checkbox>`;
        } else if (ctrl.controlType === 'select') {
          const optionsHtml = (ctrl.options ?? []).map(opt =>
            `<span role="option" data-value="${opt}">${opt}</span>`
          ).join('');
          controlHtml = `<run-select
            id="ctrl-${i}"
            value="${ctrl.currentValue ?? ''}"
            ${disabled}
            aria-labelledby="${labelId}"
            data-prop="${ctrl.name}"
          >${optionsHtml}</run-select>`;
        } else {
          const inputType = ctrl.inputType ?? 'text';
          const minAttr = ctrl.min != null ? `min="${ctrl.min}"` : '';
          const maxAttr = ctrl.max != null ? `max="${ctrl.max}"` : '';
          controlHtml = `<run-input
            id="ctrl-${i}"
            type="${inputType}"
            value="${ctrl.currentValue ?? ''}"
            ${minAttr}
            ${maxAttr}
            ${disabled}
            aria-labelledby="${labelId}"
            data-prop="${ctrl.name}"
          ></run-input>`;
        }

        return `
          <div class="control-group" role="group" aria-labelledby="${labelId}" id="${groupId}">
            <span class="control-label" id="${labelId}">${ctrl.name}</span>
            ${descriptionHtml}
            ${controlHtml}
            ${errorIndicator}
          </div>
        `;
      }).join('');

  const cssPropsPanelHtml = showCssProps
    ? `<div class="css-props-panel"><span>CSS custom properties editor — coming soon.</span></div>`
    : '';

  const slotsPanelHtml = showSlots
    ? `<div class="slots-panel"><span>Slot editor — coming soon.</span></div>`
    : '';

  const controlsPanelHtml = `
    <div part="controls">
      <div aria-label="Component controls">
        ${controlGroupsHtml}
      </div>
      ${cssPropsPanelHtml}
      ${slotsPanelHtml}
      <div class="reset-row">
        <run-button
          data-action="reset"
          ${!_isDirty || isLoading ? 'disabled' : ''}
        >Reset to defaults</run-button>
      </div>
    </div>
  `;

  // ── Preview area ──────────────────────────────────────────────────────────
  const previewInnerHtml = isError
    ? `<div role="alert" class="preview-error">${_errorMessage ?? 'An error occurred.'}</div>`
    : isLoading
      ? `<span class="preview-loading" aria-live="polite">Loading&hellip;</span>`
      : `<div class="preview-host"></div>`;

  const previewHtml = `
    <div part="preview" ${isLoading ? 'aria-busy="true"' : ''}>
      ${previewInnerHtml}
    </div>
  `;

  // ── Main grid ─────────────────────────────────────────────────────────────
  const mainGridHtml = `
    <run-grid class="main-grid">
      ${previewHtml}
      ${controlsPanelHtml}
    </run-grid>
  `;

  // ── Compact mode: code panel below grid ───────────────────────────────────
  const compactCodeHtml = (mode !== 'full' && showCode)
    ? `<div part="code"><run-code-display frameworks='${JSON.stringify(frameworks)}'></run-code-display></div>`
    : '';

  // ── Full mode: auxiliary tabs ─────────────────────────────────────────────
  let auxiliaryTabsHtml = '';
  if (mode === 'full') {
    const tabs = [];
    if (showCode)            tabs.push({ key: 'code',        label: 'Code' });
    if (showEvents)          tabs.push({ key: 'events',      label: 'Events' });
    if (showA11y)            tabs.push({ key: 'a11y',        label: 'Accessibility' });
    if (showPerformance)     tabs.push({ key: 'performance', label: 'Performance' });
    if (showTokenCompliance) tabs.push({ key: 'tokens',      label: 'Tokens' });

    if (tabs.length > 0) {
      const tabButtonsHtml = tabs.map((t, idx) =>
        `<button
          class="tab-btn"
          role="tab"
          aria-selected="${idx === 0 ? 'true' : 'false'}"
          aria-controls="tabpanel-${t.key}"
          id="tab-${t.key}"
          tabindex="${idx === 0 ? '0' : '-1'}"
          data-tab="${t.key}"
        >${t.label}</button>`
      ).join('');

      const tabPanelsHtml = tabs.map((t, idx) => {
        let panelContent = '';
        if (t.key === 'code') {
          panelContent = `<div part="code"><run-code-display frameworks='${JSON.stringify(frameworks)}'></run-code-display></div>`;
        } else if (t.key === 'events') {
          const entriesHtml = eventLog.length === 0
            ? `<span class="event-log-empty">No events captured yet.</span>`
            : `<ul class="event-log-list">${eventLog.map(ev =>
                `<li class="event-log-item"><strong>${ev.eventName}</strong> — ${new Date(ev.timestamp).toLocaleTimeString()}</li>`
              ).join('')}</ul>`;
          panelContent = `
            <div part="events">
              ${entriesHtml}
              <run-button data-action="clear-events">Clear event log</run-button>
            </div>
          `;
        } else if (t.key === 'a11y') {
          panelContent = `<div part="a11y"><span class="panel-coming-soon">Accessibility analysis — coming soon.</span></div>`;
        } else if (t.key === 'performance') {
          panelContent = `<div part="performance"><span class="panel-coming-soon">Performance analysis — coming soon.</span></div>`;
        } else if (t.key === 'tokens') {
          panelContent = `<div part="tokens"><span class="panel-coming-soon">Token compliance — coming soon.</span></div>`;
        }

        return `
          <div
            class="tab-panel"
            role="tabpanel"
            id="tabpanel-${t.key}"
            aria-labelledby="tab-${t.key}"
            ${idx !== 0 ? 'hidden' : ''}
          >${panelContent}</div>
        `;
      }).join('');

      auxiliaryTabsHtml = `
        <div class="auxiliary-tabs">
          <div class="tab-list" role="tablist" aria-label="Playground panels">
            ${tabButtonsHtml}
          </div>
          ${tabPanelsHtml}
        </div>
      `;
    }
  }

  // ── Live regions ──────────────────────────────────────────────────────────
  const liveRegionsHtml = `
    <div class="visually-hidden" aria-live="polite" aria-atomic="true" data-announce-polite></div>
    <div class="visually-hidden" role="alert" data-announce-assertive></div>
  `;

  // ── Footer slot ───────────────────────────────────────────────────────────
  const footerHtml = `<div class="footer-slot"><slot name="footer"></slot></div>`;

  // ── Assemble ──────────────────────────────────────────────────────────────
  host.shadowRoot.innerHTML = `
    <div part="container">
      ${liveRegionsHtml}
      ${headerHtml}
      ${mainGridHtml}
      ${compactCodeHtml}
      ${auxiliaryTabsHtml}
      ${footerHtml}
    </div>
  `;

  // innerHTML cannot set JS properties — `.code="..."` in a template string
  // creates a literal attribute named ".code", not a property binding.
  // Set the code property imperatively now that the elements exist in the DOM.
  if (codeText) {
    host.shadowRoot.querySelectorAll('run-code-display').forEach(cd => {
      cd.code = codeText;
    });
  }
}